import * as React from "react";
import { ResponsiveToggleContainerProps, ResponsiveToggleContainerFTNodeOptions } from "./typings";
import { Viewport, ViewSmall, ViewMedium, ViewLarge } from "@shared-ui/viewport-context";
import { BlossomComponents } from "components/flexFramework/BlossomComponents/BlossomComponents";
import { UitkExpandoCard } from "uitk-react-expando";
import { UitkSpacing } from "uitk-react-spacing";
import { UitkHeading } from "uitk-react-text";
import { getExtraClassNameBasedOnParameters } from "../SimpleContainer/utilityStyles";
import { SimpleContainerFTNodeOptions } from "components/flexComponents/SimpleContainer/typings";
import { observer } from "mobx-react";
import { withStores } from "stores";
import { normalizeOptionsData, hasRenderableChilren } from "src/components/flexFramework/FlexTemplateNode";

export interface ResponsiveToggleContainerContext {
  modulesHaveBorder?: boolean;
}

const defaultContext = {
  modulesHaveBorder: false,
};

export const ResponsiveToggleContainerContext = React.createContext<ResponsiveToggleContainerContext>(defaultContext);

const DEFAULT_WIDTH = "100";

export const ResponsiveToggleContainer = withStores(
  "flexModuleModelStore",
  "context"
)(
  observer((props: ResponsiveToggleContainerProps) => {
    const { context, blossomComponent, flexModuleModelStore, flexTemplateNode } = props;

    /* istanbul ignore next */
    if (!blossomComponent) {
      return null;
    }

    const options = normalizeOptionsData(
      flexTemplateNode,
      flexModuleModelStore.getModel(flexTemplateNode.id)
    ) as ResponsiveToggleContainerFTNodeOptions;

    const {
      containerName,
      mobileWidth,
      title,
      width,
      innerTitle,
      tabletWidth,
      desktopWidth,
      collapsed,
      hasBorder,
    } = options;

    if (!hasRenderableChilren(flexTemplateNode, flexModuleModelStore)) {
      return null;
    }

    const gridWidth = regionWidthToGridMap[width || DEFAULT_WIDTH];
    const gridTabletWidth = regionWidthToGridMap[tabletWidth || DEFAULT_WIDTH];
    const gridMobileWidth = regionWidthToGridMap[mobileWidth || DEFAULT_WIDTH];
    const commonClassNames = [
      "uitk-grid uitk-cell",
      getExtraClassNameBasedOnParameters(options as SimpleContainerFTNodeOptions),
    ]
      .filter((classes) => classes !== "")
      .join(" ");

    const ResponsiveContainerContent = (containerProps: { includeTitle: boolean; classNames: string }) => (
      <div className={` ${commonClassNames} ${containerProps.classNames} `} role="region">
        {title && (
          <UitkSpacing>
            <UitkHeading
              tag="h2"
              size={3}
              className={` ${!containerProps.includeTitle && "is-visually-hidden"} uitk-cell all-cell-1-1`}
            >
              {title}
            </UitkHeading>
          </UitkSpacing>
        )}
        {innerTitle && (
          <UitkSpacing padding={{ inlinestart: "three" }}>
            <UitkHeading tag="h3" size={3} className="uitk-cell all-cell-1-1">
              {innerTitle}
            </UitkHeading>
          </UitkSpacing>
        )}
        <BlossomComponents blossomComponent={blossomComponent} context={context} />
      </div>
    );
    const [isVisible, setIsVisible] = React.useState(!collapsed);
    const toggle = React.useCallback(() => setIsVisible(!isVisible), [isVisible]);

    const flexContent = (
      <>
        {title && (
          <UitkSpacing>
            <UitkHeading tag="h2" size={3} className="uitk-cell all-cell-1-1">
              {title}
            </UitkHeading>
          </UitkSpacing>
        )}
        {innerTitle && (
          <UitkSpacing padding={{ inlinestart: "three" }}>
            <UitkHeading tag="h3" size={3} className="uitk-cell all-cell-1-1">
              {innerTitle}
            </UitkHeading>
          </UitkSpacing>
        )}
        <BlossomComponents blossomComponent={blossomComponent} context={context} />
      </>
    );

    return (
      <ResponsiveToggleContainerContext.Provider value={{ modulesHaveBorder: hasBorder }}>
        <Viewport>
          {mobileWidth === "hidden" ? (
            <ViewSmall />
          ) : (
            <ViewSmall>
              <UitkSpacing padding={{ block: "three", inline: "three" }}>
                <div className="ResponsiveToggleContainer SimpleContainer">
                  <UitkExpandoCard
                    isVisible={isVisible}
                    handleClick={toggle}
                    expandoTitle={title}
                    expandoDescription={
                      <ResponsiveContainerContent
                        classNames={`SimpleContainer ${
                          containerName === "dx-page-header" ? " elevation-4" : ""
                        } ${gridMobileWidth}`}
                        includeTitle={false}
                      />
                    }
                  />
                </div>
              </UitkSpacing>
            </ViewSmall>
          )}

          {tabletWidth === "hidden" ? (
            <ViewMedium />
          ) : (
            <ViewMedium>
              <UitkSpacing padding={{ block: "three", inline: "three" }}>
                <div
                  className={`ResponsiveToggleContainer SimpleContainer ${
                    containerName === "dx-page-header" ? " elevation-4" : ""
                  } ${commonClassNames} ${gridTabletWidth} `}
                  role="region"
                >
                  {flexContent}
                </div>
              </UitkSpacing>
            </ViewMedium>
          )}

          {desktopWidth === "hidden" ? (
            <ViewLarge />
          ) : (
            <ViewLarge>
              <UitkSpacing padding={{ block: "three", inline: "three" }}>
                <div
                  className={`${commonClassNames} ${gridWidth} ${
                    containerName === "dx-page-header" ? "elevation-4" : " SimpleContainer ResponsiveToggleContainer"
                  }`}
                  role="region"
                >
                  {flexContent}
                </div>
              </UitkSpacing>
            </ViewLarge>
          )}
        </Viewport>
      </ResponsiveToggleContainerContext.Provider>
    );
  })
);

/**
 * Return the classes corresponding to a specific region. This is
 * a temporary solution until Module groups are implemented
 * @param {string} regionName - Name of region
 * @return {string} classes to decorate region
 */
/* istanbul ignore next */

const regionWidthToGridMap = {
  "25": "all-cell-1-4",
  "33": "all-cell-1-3",
  "50": "all-cell-1-2",
  "66": "all-cell-2-3",
  "75": "all-cell-3-4",
  "100": "all-cell-1-1",
};

export default ResponsiveToggleContainer;
